import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Unable to sign in");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex items-center justify-center bg-white px-6 py-12">
        <div className="w-full max-w-sm">
          <Link
            to="/"
            data-testid="login-back-to-site"
            className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-[#526E7E] transition-colors hover:text-[#18344A]"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Site
          </Link>

          <div className="mb-8">
            <img src="/assets/logo-dark.png" alt="Pack Brothers Hospitality" className="h-24 w-auto sm:h-28" />
          </div>

          <h1 className="font-display text-2xl font-semibold tracking-tight text-[#18344A]">Welcome back</h1>
          <p className="mt-1 text-sm text-[rgba(48,56,60,0.65)]">Sign in to your dashboard.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                required
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@packbrothers.com"
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="rounded-[10px] border border-[rgba(190,60,60,0.25)] bg-[rgba(190,60,60,0.08)] px-3 py-2 text-sm text-[#9a3a3a]"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="h-11 w-full rounded-[10px] bg-[#18344A] text-white hover:bg-[#18344A]/90"
            >
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Sign in
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-[rgba(48,56,60,0.5)]">
            Access is restricted to Pack Brothers staff.
          </p>
        </div>
      </div>

      {/* Right: image panel */}
      <div className="relative hidden lg:block">
        <img
          src="/assets/login-hero.jpg"
          alt="Pack Brothers Hospitality founders"
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#18344A]/85 via-[#18344A]/25 to-[#18344A]/10" />
        <div className="pb-grad-orbs absolute inset-0" />
        <div className="absolute bottom-0 left-0 right-0 p-12">
          <h2 className="font-display text-3xl font-semibold leading-tight text-white">
            One command center for eight waterfront destinations.
          </h2>
          <p className="mt-3 max-w-md text-sm text-white/80">
            Reservations, events, revenue, and growth — unified across the Pack Brothers Hospitality group.
          </p>
        </div>
      </div>
    </div>
  );
}
